<script setup lang="ts">
type LegendItemValue = string | number | (string | number)[]

interface LegendItem {
    label: string
    value: LegendItemValue
    color?: string
}

const props = defineProps<{
    dimension?: Dimension
    activityPieces: LegendItem[]
    indicatorPieces: LegendItem[]
}>()

/**
 * Translations.
 */
const { t } = useI18n()

/**
 * Use query params.
 */
const { config } = useParams()

/**
 * Use metadata.
 */
const { meta } = useMeta()

/**
 * Use countries.
 */
const { byRegion } = useCountries()

/**
 * Whether the current dimension is an activity
 * and the selected mode is map.
 */
const isActivityMap = computed(() => {
    return config.viewMode === 'map' && props.dimension?.type === 'activity'
})

/**
 * Whether the current dimension is an indicator
 * and the selected mode is map.
 */
const isIndicatorMap = computed(() => {
    return config.viewMode === 'map' && props.dimension?.type === 'indicator'
})

/**
 * By default, the legend will display
 * the list of regions.
 */
const defaultLegendItems = computed(() => {
    return meta.regions.map(region => ({
        label: region.name,
        value: unref(byRegion).get(region.id) ?? [],
        color: region.color
    }))
})

/**
 * List of legend items.
 */
const items = computed(() => {
    if (unref(isActivityMap)) {
        return props.activityPieces
    } else if (unref(isIndicatorMap)) {
        return props.indicatorPieces
    }

    return unref(defaultLegendItems)
})

/**
 * Selected items.
 */
const selected = computed<(string | number)[] | undefined>({
    get() {
        if (unref(isActivityMap)) {
            return config.activityStatus
        } else if (unref(isIndicatorMap)) {
            return config.indicatorStatus
        }

        return config.compareCountries
    },
    set(value) {
        if (unref(isActivityMap)) {
            config.activityStatus = value as number[]
        } else if (unref(isIndicatorMap)) {
            config.indicatorStatus = value as number[]
        } else {
            config.compareCountries = value as string[]
        }
    }
})

/**
 * Flat array of values.
 */
const allValues = computed(() => {
    return unref(items)?.flatMap(({ value }) => value as (string | number)[])
})

/**
 * Check whether an item is selected.
 */
function isSelected(item: LegendItem) {
    if (!unref(selected)) return true

    return Array.isArray(item.value)
        ? item.value.every(value => unref(selected)?.includes(value))
        : unref(selected)?.includes(item.value)
}

/**
 * Toggle an item selection.
 */
function toggle(item: LegendItem) {
    const values = unref(selected) ?? unref(allValues)

    if (isSelected(item)) {
        const itemValue = Array.isArray(item.value) ? item.value : [item.value]
        if (selected.value?.length === itemValue.length) {
            selected.value = []
        } else {
            selected.value = itemValue
        }
    } else {
        selected.value = values.concat(item.value)
    }
}
</script>

<template>
    <div
        class="flex flex-col justify-center"
        data-tutorial-step="10"
        :data-tutorial-text="t('tutorial-legend')"
    >
        <h1 class="mb-2 hidden text-sm font-bold xl:block">
            {{ t('legend') }}
        </h1>

        <div
            class="flex grid-cols-2 flex-row flex-wrap justify-center gap-2 text-xs xl:flex-col xl:gap-x-4 xl:screen-ratio-[2:1]:flex-row"
        >
            <div
                v-for="(item, index) in items"
                :key="index"
                class="flex flex-shrink-0 cursor-pointer items-center gap-2"
                :class="[isSelected(item) ? '' : 'opacity-50']"
                @click="toggle(item)"
            >
                <div
                    class="h-4 w-4 rounded-full"
                    :style="{
                        backgroundColor: item.color
                    }"
                />
                <span>{{ item.label }}</span>
            </div>
        </div>
    </div>
</template>